/**
 * Voice Settings Tab
 *
 * Manages voice and audio settings:
 * - Audio device selection (microphone, speakers)
 * - Audio quality toggles (noise suppression, echo cancellation, AGC)
 * - Push-to-talk configuration
 */

import { useState } from 'react';
import { useUserSettings, useUpdateSettings } from '@/hooks/useUserSettings';
import { useAudioDevices } from '@/hooks/useAudioDevices';

const PUSH_TO_TALK_KEYS = [
  { value: 'Space', label: 'Space' },
  { value: 'ControlLeft', label: 'Left Ctrl' },
  { value: 'ControlRight', label: 'Right Ctrl' },
  { value: 'ShiftLeft', label: 'Left Shift' },
  { value: 'ShiftRight', label: 'Right Shift' },
  { value: 'AltLeft', label: 'Left Alt' },
  { value: 'AltRight', label: 'Right Alt' },
  { value: 'KeyV', label: 'V' },
  { value: 'KeyT', label: 'T' },
];

export const VoiceSettings = () => {
  const { data: settings } = useUserSettings();
  const updateSettings = useUpdateSettings();
  const { microphones, speakers, isLoading: devicesLoading, error: devicesError } = useAudioDevices();

  const [inputDevice, setInputDevice] = useState<string | null>(null);
  const [outputDevice, setOutputDevice] = useState<string | null>(null);
  const [noiseSuppression, setNoiseSuppression] = useState(true);
  const [echoCancellation, setEchoCancellation] = useState(true);
  const [autoGainControl, setAutoGainControl] = useState(true);
  const [pushToTalkEnabled, setPushToTalkEnabled] = useState(false);
  const [pushToTalkKey, setPushToTalkKey] = useState('Space');

  // Sync form state from fetched settings (adjust state during render on change)
  const [syncedSettings, setSyncedSettings] = useState(settings);
  if (settings && settings !== syncedSettings) {
    setSyncedSettings(settings);
    setInputDevice(settings.voice_input_device);
    setOutputDevice(settings.voice_output_device);
    setNoiseSuppression(settings.noise_suppression);
    setEchoCancellation(settings.echo_cancellation);
    setAutoGainControl(settings.auto_gain_control);
    setPushToTalkEnabled(settings.push_to_talk_enabled);
    setPushToTalkKey(settings.push_to_talk_key);
  }

  const handleUpdate = async (field: string, value: string | number | boolean | null) => {
    try {
      await updateSettings.mutateAsync({ [field]: value });
    } catch (error) {
      console.error('Failed to update voice settings:', error);
    }
  };

  const renderToggle = (
    label: string,
    description: string,
    value: boolean,
    onChange: (value: boolean) => void,
    field: string
  ) => (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <button
        onClick={() => {
          const newValue = !value;
          onChange(newValue);
          handleUpdate(field, newValue);
        }}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          value ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            value ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Devices Error */}
      {devicesError && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">{devicesError}</p>
          <p className="text-xs text-yellow-600 mt-1">
            Моля, разрешете достъп до микрофона, за да видите устройствата
          </p>
        </div>
      )}

      {/* Audio Devices Section */}
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-gray-900">Аудио устройства</h3>

        {devicesLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            <p className="ml-3 text-sm text-gray-500">Зареждане на устройства...</p>
          </div>
        ) : (
          <>
            {/* Microphone Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Микрофон
              </label>
              <select
                value={inputDevice || ''}
                onChange={(e) => {
                  const value = e.target.value || null;
                  setInputDevice(value);
                  handleUpdate('voice_input_device', value);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">По подразбиране</option>
                {microphones.map((mic) => (
                  <option key={mic.deviceId} value={mic.deviceId}>
                    {mic.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Speaker Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Високоговорител
              </label>
              <select
                value={outputDevice || ''}
                onChange={(e) => {
                  const value = e.target.value || null;
                  setOutputDevice(value);
                  handleUpdate('voice_output_device', value);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">По подразбиране</option>
                {speakers.map((speaker) => (
                  <option key={speaker.deviceId} value={speaker.deviceId}>
                    {speaker.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      {/* Audio Quality Section */}
      <div className="space-y-2 pt-4 border-t border-gray-200">
        <h3 className="text-base font-semibold text-gray-900">Качество на звука</h3>
        <p className="text-xs text-gray-500 mb-3">
          Настройте обработката на аудиото за по-добро качество на обажданията
        </p>

        {renderToggle(
          'Потискане на шум',
          'Намалява фоновия шум',
          noiseSuppression,
          setNoiseSuppression,
          'noise_suppression'
        )}

        {renderToggle(
          'Премахване на ехо',
          'Елиминира обратна връзка',
          echoCancellation,
          setEchoCancellation,
          'echo_cancellation'
        )}

        {renderToggle(
          'Автоматично усилване',
          'Балансира силата на звука',
          autoGainControl,
          setAutoGainControl,
          'auto_gain_control'
        )}
      </div>

      {/* Push to Talk Section */}
      <div className="space-y-4 pt-4 border-t border-gray-200">
        <h3 className="text-base font-semibold text-gray-900">Push to Talk</h3>
        <p className="text-xs text-gray-500 mb-3">
          Задръжте клавиш за говорене вместо винаги да сте активни
        </p>

        {renderToggle(
          'Активирай Push to Talk',
          'Микрофонът ще бъде изключен, освен когато задържате клавиша',
          pushToTalkEnabled,
          setPushToTalkEnabled,
          'push_to_talk_enabled'
        )}

        {pushToTalkEnabled && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Клавиш за Push to Talk
            </label>
            <select
              value={pushToTalkKey}
              onChange={(e) => {
                const value = e.target.value;
                setPushToTalkKey(value);
                handleUpdate('push_to_talk_key', value);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              {PUSH_TO_TALK_KEYS.map((key) => (
                <option key={key.value} value={key.value}>
                  {key.label}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-500">
              Задръжте избрания клавиш, за да активирате микрофона
            </p>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-900 font-medium">ℹ️ Забележка</p>
        <p className="text-xs text-blue-700 mt-1">
          Промените в настройките ще се приложат автоматично към всички активни
          гласови обаждания. Ще трябва да влезете отново в гласов канал, за да
          приложите промени в устройствата.
        </p>
      </div>
    </div>
  );
};

export default VoiceSettings;
